import { CircularProgress, Typography, keyframes, styled } from "@mui/material"
import { extraColors } from "../../../utils/theme"
import Button from "../../Shared/Button"
import { useListAssets } from "../../WatchedLocations/hooks/useAssetService"
import { ExpertInsights } from "./ExpertInsights/ExpertInsights"

import { SettingsContainer } from "../shared/SettingsContainer"
import {
  NotificationContextProvider,
  useNotificationContext,
} from "./contexts/NotificationContext"

export const fadeInOutAnimation = keyframes`
0% {
  opacity: .3;
}
50% {
  opacity: 1;
}
100% {
  opacity: .3;
}
`

const Container = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  gap: 24,
  paddingBottom: 12,
  maxWidth: 800,
  alignContent: "center",
  justifyContent: "center",
}))

const ToolBar = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  bottom: 0,
  right: 0,
  left: 0,
  padding: "0 24px",
  backgroundColor: extraColors.translucent5,
  height: "72px",
  [theme.breakpoints.up("md")]: {
    justifyContent: "end",
    paddingRight: 80,
  },
}))

const UnsavedText = styled(Typography)({
  animation: `${fadeInOutAnimation} 4s ease infinite`,
  color: extraColors.status.orangeMiddle,
  marginRight: 40,
})

const SettingsContainerStyled = styled(SettingsContainer)({
  height: 300,
})

const ProgressContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  alignSelf: "center",
  height: "100%",
})

export const Notifications = () => {
  const { data: assets, isLoading } = useListAssets({
    includeGlobal: true,
  })

  if (isLoading)
    return (
      <Container>
        <ContainersLoading title="Expert Insights" />
        <NotificationSettingsActionsLoading />
      </Container>
    )

  return (
    <NotificationContextProvider assets={assets.assets}>
      <Container>
        <ExpertInsights />
      </Container>
      <NotificationSettingsActions />
    </NotificationContextProvider>
  )
}

const NotificationSettingsActions: React.FC = () => {
  const {
    submitNotificationSettings,
    discardNotificationSettings,
    hasChangedSettings,
    isSaving,
  } = useNotificationContext()
  return (
    <ToolBar>
      {(hasChangedSettings || isSaving) && (
        <UnsavedText variant="body1Bold">
          {isSaving ? "Saving settings" : "You have unsaved settings"}
        </UnsavedText>
      )}
      <Button
        variant="outlined"
        onClick={discardNotificationSettings}
        disabled={!hasChangedSettings || isSaving}
        sx={{ marginRight: "40px" }}
      >
        Discard Changes
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => void submitNotificationSettings()}
        disabled={!hasChangedSettings || isSaving}
      >
        Submit
      </Button>
    </ToolBar>
  )
}

const NotificationSettingsActionsLoading: React.FC = () => {
  return (
    <ToolBar>
      <Button
        variant="outlined"
        disabled
        sx={{ marginRight: "40px", minWidth: "fit-content" }}
      >
        Discard Changes
      </Button>
      <Button variant="contained" color="primary" disabled>
        Submit
      </Button>
    </ToolBar>
  )
}

const ContainersLoading: React.FC<{ title: string }> = ({ title }) => {
  return (
    <SettingsContainerStyled title={title}>
      <ProgressContainer>
        <CircularProgress sx={{ color: extraColors.purpleMiddle }} />
      </ProgressContainer>
    </SettingsContainerStyled>
  )
}
